import { useMemo } from 'react';

import { useLocalizedStrings } from 'app/localization/Context';

import { DeviceType } from 'app/model/Device';

import { ReactComponent as IconCooler } from 'images/device/symbol/hinava_gradient/cooler.svg';
import { ReactComponent as IconDoor } from 'images/device/symbol/hinava_gradient/door.svg';
import { ReactComponent as IconDoorBLE } from 'images/device/symbol/hinava_gradient/door-ble.svg';
import { ReactComponent as IconDoorWifi } from 'images/device/symbol/hinava_gradient/door-wifi.svg';
import { ReactComponent as IconMotion } from 'images/device/symbol/hinava_gradient/motion.svg';
import { ReactComponent as IconMotion2 } from 'images/device/symbol/hinava_gradient/motion2.svg';
import { ReactComponent as IconMotion3 } from 'images/device/symbol/hinava_gradient/motion3.svg';
import { ReactComponent as IconLamp } from 'images/device/symbol/hinava_gradient/lamp.svg';
import { ReactComponent as IconSwitchClassic } from 'images/device/symbol/hinava_gradient/switch-classic.svg';
import { ReactComponent as IconSwitchTitan } from 'images/device/symbol/hinava_gradient/switch-titan.svg';
import { ReactComponent as IconSwitchLite } from 'images/device/symbol/hinava_gradient/switch-lite.svg';
import { ReactComponent as IconScenarioSwitch } from 'images/device/symbol/hinava_gradient/scenario-switch.svg';
import { ReactComponent as IconDoorbell } from 'images/device/symbol/hinava_gradient/doorbell.svg';
import { ReactComponent as IconElevator } from 'images/device/symbol/hinava_gradient/elevator.svg';
import { ReactComponent as IconCurtain } from 'images/device/symbol/hinava_gradient/curtain.svg';
import { ReactComponent as IconRelay } from 'images/device/symbol/hinava_gradient/relay.svg';
import { ReactComponent as IconPlug } from 'images/device/symbol/hinava_gradient/plug.svg';
import { ReactComponent as IconSiren } from 'images/device/symbol/hinava_gradient/siren.svg';
import { ReactComponent as IconMultipurposeSensor } from 'images/device/symbol/hinava_gradient/multipurpose-sensor.svg';
import { ReactComponent as IconTermostat } from 'images/device/symbol/hinava_gradient/termostat.svg';
import { ReactComponent as IconRemote } from 'images/device/symbol/hinava_gradient/remote.svg';
import { ReactComponent as IconAudioController } from 'images/device/symbol/hinava_gradient/audio-controller.svg';
import { ReactComponent as IconWC } from 'images/device/symbol/hinava_gradient/wc.svg';

import { ReactComponent as IconCoolerWhite } from 'images/device/symbol/white/cooler.svg';
import { ReactComponent as IconDoorWhite } from 'images/device/symbol/white/door.svg';
import { ReactComponent as IconMotionWhite } from 'images/device/symbol/white/motion.svg';
import { ReactComponent as IconLampWhite } from 'images/device/symbol/white/lamp.svg';
import { ReactComponent as IconSwitchClassicWhite } from 'images/device/symbol/white/switch-classic.svg';
import { ReactComponent as IconSwitchTitanWhite } from 'images/device/symbol/white/switch-titan.svg';
import { ReactComponent as IconSwitchLiteWhite } from 'images/device/symbol/white/switch-lite.svg';
import { ReactComponent as IconScenarioSwitchWhite } from 'images/device/symbol/white/scenario-switch.svg';
import { ReactComponent as IconDoorbellWhite } from 'images/device/symbol/white/doorbell.svg';
import { ReactComponent as IconElevatorWhite } from 'images/device/symbol/white/elevator.svg';
import { ReactComponent as IconCurtainWhite } from 'images/device/symbol/white/curtain.svg';
import { ReactComponent as IconRelayWhite } from 'images/device/symbol/white/relay.svg';
import { ReactComponent as IconPlugWhite } from 'images/device/symbol/white/plug.svg';
import { ReactComponent as IconSirenWhite } from 'images/device/symbol/white/siren.svg';
import { ReactComponent as IconMultipurposeSensorWhite } from 'images/device/symbol/white/multipurpose-sensor.svg';
import { ReactComponent as IconTermostatWhite } from 'images/device/symbol/white/termostat.svg';
import { ReactComponent as IconRemoteWhite } from 'images/device/symbol/white/remote.svg';
import { ReactComponent as IconAudioControllerWhite } from 'images/device/symbol/white/audio-controller.svg';
import { ReactComponent as IconWCWhite } from 'images/device/symbol/white/wc.svg';

import { ReactComponent as ImageResetUserManual } from 'images/device/reset-instruction/user-manual.svg';
import { ReactComponent as ImageResetRemote } from 'images/device/reset-instruction/remote.svg';
import { ReactComponent as ImageResetCooler } from 'images/device/reset-instruction/cooler.svg';
import { ReactComponent as ImageResetDoorBLE } from 'images/device/reset-instruction/door-ble.svg';
import { ReactComponent as ImageResetDoorWifi } from 'images/device/reset-instruction/door-wifi.svg';
import { ReactComponent as ImageResetPlug } from 'images/device/reset-instruction/plug.svg';
import { ReactComponent as ImageResetClassicSwitch } from 'images/device/reset-instruction/switch-classic.svg';
import { ReactComponent as ImageResetTitanSwitch } from 'images/device/reset-instruction/switch-titan.svg';
import { ReactComponent as ImageResetLiteSwitch } from 'images/device/reset-instruction/switch-lite.svg';
import { ReactComponent as ImageResetScenarioSwitch } from 'images/device/reset-instruction/scenario-switch.svg';
import { ReactComponent as ImageResetCurtain } from 'images/device/reset-instruction/curtain.svg';
import { ReactComponent as ImageResetRelay } from 'images/device/reset-instruction/relay4.svg';
import { ReactComponent as ImageResetMotion } from 'images/device/reset-instruction/motion.svg';
import { ReactComponent as ImageResetMotionWifi } from 'images/device/reset-instruction/motion-wifi.svg';
import { ReactComponent as ImageResetSiren } from 'images/device/reset-instruction/siren.svg';
import { ReactComponent as ImageResetAudioController } from 'images/device/reset-instruction/audio-controller.svg';
import { ReactComponent as ImageResetHinavaEcoThermostat } from 'images/device/reset-instruction/hinava-eco-thermostat.svg';


export const AddDeviceCategory = {
    TouchSwitches: "touchSwitches",
    SecurityDevices: "securityDevices",
    TemperatureControlDevices: "temperatureControlDevices",
    ElectricityControlDevices: "electricityControlDevices",
    ConvenienceDevices: "convenienceDevices",
};

export function useAddDeviceConfig(negotiatedProtocol) {
    const ad = useLocalizedStrings().addDeviceProcess;

    const deviceConfig = useMemo(() => [
        {
            hasLevel2: true,
            isAvailable: true,
            name: ad.selectSwitch.categoryName,
            categories: [AddDeviceCategory.TouchSwitches],
            icon: IconSwitchTitan,
            selectPageTitle: ad.selectSwitch.selectPageTitle,
            types: [{
                name: ad.selectSwitch.titanName,
                wifi: true,
                pluggable: true,
                isAvailable: true,
                icon: IconSwitchTitan, iconWhite: IconSwitchTitanWhite,
                resetInstruction: ad.resetInstruction.titanSwitch, resetImage: ImageResetTitanSwitch,
                resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
                searchTypes: negotiatedProtocol.canAddTitanLampX3 ?
                    [DeviceType.lamp, DeviceType.lampX1TwoWay, DeviceType.dpst, DeviceType.lampX3, DeviceType.lampX4, DeviceType.curtain1Lamp2] :
                    [DeviceType.lamp, DeviceType.lampX1TwoWay, DeviceType.dpst, DeviceType.lampX4, DeviceType.curtain1Lamp2]
            }, {
                name: ad.selectSwitch.liteName,
                wifi: true,
                pluggable: true,
                isAvailable: negotiatedProtocol.hasLiteSwitchInAddDevice,
                icon: IconSwitchLite, iconWhite: IconSwitchLiteWhite,
                resetInstruction: ad.resetInstruction.liteSwitch, resetImage: ImageResetLiteSwitch,
                resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
                searchTypes: [DeviceType.lamp, DeviceType.lampX1TwoWay, DeviceType.dpst, DeviceType.lampX3, DeviceType.lampX4, DeviceType.curtain1Lamp2]
            }, {
                name: ad.selectSwitch.classicName,
                wifi: true,
                pluggable: true,
                isAvailable: true,
                icon: IconSwitchClassic, iconWhite: IconSwitchClassicWhite,
                resetInstruction: ad.resetInstruction.classicSwitch, resetImage: ImageResetClassicSwitch,
                resetInstructionConfirm: ad.resetInstructionConfirm.blinkedLikeFigure,
                searchTypes: [DeviceType.dpst]
            }]
        }, {
            name: ad.types.scenarioSwitch,
            categories: [AddDeviceCategory.TouchSwitches],
            isAvailable: negotiatedProtocol.hasScenarioSwitch,
            wifi: true,
            pluggable: true,
            icon: IconScenarioSwitch, iconWhite: IconScenarioSwitchWhite,
            resetInstruction: ad.resetInstruction.scenarioSwitch, resetImage: ImageResetScenarioSwitch,
            resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
            searchTypes: [DeviceType.scenarioSwitch]
        }, {
            name: ad.types.doorbell,
            categories: [AddDeviceCategory.TouchSwitches],
            isAvailable: negotiatedProtocol.hasDoorbell,
            wifi: true,
            pluggable: true,
            icon: IconDoorbell, iconWhite: IconDoorbellWhite,
            resetInstruction: ad.resetInstruction.general, resetImage: ImageResetUserManual,
            resetInstructionConfirm: ad.resetInstructionConfirm.onReadyToSetupPhase,
            searchTypes: [DeviceType.doorbell]
        }, {
            name: ad.types.elevator,
            categories: [AddDeviceCategory.TouchSwitches],
            isAvailable: negotiatedProtocol.hasElevator1,
            wifi: true,
            pluggable: true,
            icon: IconElevator, iconWhite: IconElevatorWhite,
            resetInstruction: ad.resetInstruction.general, resetImage: ImageResetUserManual,
            resetInstructionConfirm: ad.resetInstructionConfirm.onReadyToSetupPhase,
            searchTypes: [DeviceType.elevator1]
        }, {
            name: ad.types.wc,
            categories: [AddDeviceCategory.TouchSwitches],
            isAvailable: negotiatedProtocol.hasWC,
            wifi: true,
            pluggable: true,
            icon: IconWC, iconWhite: IconWCWhite,
            resetInstruction: ad.resetInstruction.general, resetImage: ImageResetUserManual,
            resetInstructionConfirm: ad.resetInstructionConfirm.onReadyToSetupPhase,
            searchTypes: [DeviceType.wc]
        }, {
            name: ad.types.curtain,
            categories: [AddDeviceCategory.TouchSwitches],
            isAvailable: negotiatedProtocol.hasCurtain,
            wifi: true,
            pluggable: true,
            icon: IconCurtain, iconWhite: IconCurtainWhite,
            resetInstruction: ad.resetInstruction.curtain, resetImage: ImageResetCurtain,
            resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
            searchTypes: [DeviceType.curtain, DeviceType.curtain1Lamp2]
        }, {
            name: ad.types.evaporativeCooler,
            categories: [AddDeviceCategory.TouchSwitches, AddDeviceCategory.TemperatureControlDevices],
            isAvailable: negotiatedProtocol.hasEvaporativeCooler,
            wifi: true,
            pluggable: true,
            icon: IconCooler, iconWhite: IconCoolerWhite,
            resetInstruction: ad.resetInstruction.evaporativeCooler, resetImage: ImageResetCooler,
            resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
            searchTypes: [DeviceType.evaporativeCooler]
        }, {
            hasLevel2: true,
            isAvailable: true,
            name: ad.types.thermostat,
            categories: [AddDeviceCategory.TemperatureControlDevices],
            icon: IconTermostat,
            selectPageTitle: ad.selectSwitch.selectPageTitle,
            types: [{
                name: ad.types.hinavaEcoThermostat,
                isAvailable: negotiatedProtocol.hasEcoThermostat,
                wifi: true,
                pluggable: true,
                icon: IconTermostat, iconWhite: IconTermostatWhite,
                resetInstruction: ad.resetInstruction.hinavaEcoThermostat, resetImage: ImageResetHinavaEcoThermostat, customResetImageStyle: { marginTop: 24 },
                resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
                searchTypes: [DeviceType.hinavaEcoThermostat]
            }, {
                name: ad.types.sednaThermostat,
                isAvailable: true,
                wifi: true,
                pluggable: true,
                icon: IconTermostat, iconWhite: IconTermostatWhite,
                resetInstruction: ad.resetInstruction.general, resetImage: ImageResetUserManual, customResetImageStyle: { maxHeight: 150, paddingTop: 16 },
                resetInstructionConfirm: ad.resetInstructionConfirm.onReadyToSetupPhase,
                searchTypes: [DeviceType.sedna, DeviceType.marsus]
            }]
        }, {
            name: ad.types.multipurposeSensor,
            categories: [AddDeviceCategory.SecurityDevices],
            isAvailable: negotiatedProtocol.hasMultipurposeSensor,
            wifi: true,
            pluggable: true,
            icon: IconMultipurposeSensor, iconWhite: IconMultipurposeSensorWhite,
            resetInstruction: ad.resetInstruction.general, resetImage: ImageResetUserManual, customResetImageStyle: { maxHeight: 150, paddingTop: 16 },
            resetInstructionConfirm: ad.resetInstructionConfirm.onReadyToSetupPhase,
            searchTypes: [DeviceType.multipurposeSensor]
        }, {
            hasLevel2: true,
            isAvailable: true,
            name: ad.selectDoor.categoryName,
            categories: [AddDeviceCategory.SecurityDevices],
            icon: IconDoor,
            selectPageTitle: ad.selectDoor.selectPageTitle,
            types: [{
                name: ad.selectDoor.wifiName,
                wifi: true,
                pluggable: true,
                isAvailable: negotiatedProtocol.hasWifiDoorSensor,
                icon: IconDoorWifi, iconWhite: IconDoorWhite,
                resetInstruction: ad.resetInstruction.doorWifi, resetImage: ImageResetDoorWifi,
                resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
                searchTypes: [DeviceType.wifiDoorSensor]
            }, {
                name: ad.selectDoor.bleName,
                wifi: false,
                pluggable: false,
                isAvailable: true,
                icon: IconDoorBLE, iconWhite: IconDoorWhite,
                resetInstruction: ad.resetInstruction.door, resetImage: ImageResetDoorBLE,
                resetInstructionConfirm: ad.resetInstructionConfirm.blinkedLikeFigure,
                searchTypes: [DeviceType.doorSensor]
            }]
        }, {
            hasLevel2: true,
            isAvailable: true,
            name: ad.selectMotion.categoryName,
            categories: [AddDeviceCategory.SecurityDevices],
            icon: IconMotion,
            selectPageTitle: ad.selectMotion.selectPageTitle,
            types: [{
                name: ad.selectMotion.wifiName,
                wifi: true,
                pluggable: true,
                isAvailable: negotiatedProtocol.hasWifiMotionSensor,
                icon: IconMotion2, iconWhite: IconMotionWhite,
                resetInstruction: ad.resetInstruction.motionWifi, resetImage: ImageResetMotionWifi,
                resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
                searchTypes: [DeviceType.wifiMotionSensor]
            }, {
                name: ad.selectMotion.bleName,
                wifi: false,
                pluggable: false,
                isAvailable: true,
                icon: IconMotion3, iconWhite: IconMotionWhite,
                resetInstruction: ad.resetInstruction.motion, resetImage: ImageResetMotion,
                resetInstructionConfirm: ad.resetInstructionConfirm.blinkedLikeFigure,
                searchTypes: [DeviceType.motion]
            }]
        }, {
            name: ad.types.relay,
            categories: [AddDeviceCategory.ElectricityControlDevices],
            isAvailable: negotiatedProtocol.hasRelay4,
            wifi: true,
            pluggable: true,
            icon: IconRelay, iconWhite: IconRelayWhite,
            resetInstruction: ad.resetInstruction.relay, resetImage: ImageResetRelay,
            resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
            searchTypes: [DeviceType.relay4]
        }, {
            name: ad.types.siren,
            categories: [AddDeviceCategory.SecurityDevices],
            isAvailable: true,
            wifi: true,
            pluggable: true,
            icon: IconSiren, iconWhite: IconSirenWhite,
            resetInstruction: ad.resetInstruction.siren, resetImage: ImageResetSiren,
            resetInstructionConfirm: ad.resetInstructionConfirm.fastBlinkingLikeFigure,
            searchTypes: [DeviceType.siren]
        }, {
            name: ad.types.audioController,
            categories: [AddDeviceCategory.ConvenienceDevices],
            isAvailable: negotiatedProtocol.hasAudioController,
            wifi: true,
            pluggable: true,
            icon: IconAudioController, iconWhite: IconAudioControllerWhite,
            resetInstruction: ad.resetInstruction.audioController, resetImage: ImageResetAudioController,
            resetInstructionConfirm: ad.resetInstructionConfirm.blinkingLikeFigure,
            searchTypes: [DeviceType.amplifier]
        }, {
            name: ad.types.remote,
            categories: [AddDeviceCategory.ConvenienceDevices, AddDeviceCategory.TemperatureControlDevices],
            isAvailable: negotiatedProtocol.hasRemote,
            wifi: true,
            pluggable: true,
            icon: IconRemote, iconWhite: IconRemoteWhite,
            resetInstruction: ad.resetInstruction.remote, resetImage: ImageResetRemote,
            resetInstructionConfirm: ad.resetInstructionConfirm.blinkingLikeFigure,
            searchTypes: [DeviceType.remote]
            // }, {
            //     name: ad.types.tuyaPhysicalRemote,
            //     isAvailable: (negotiatedProtocol.hasTuya),
            //     wifi: true,
            //     pluggable: true,
            //     icon: IconRemote, iconWhite: IconRemoteWhite,
            //     resetInstruction: ad.resetInstruction.general, resetImage: ImageResetUserManual, customResetImageStyle: { maxHeight: 150, paddingTop: 16 },
            //     resetInstructionConfirm: ad.resetInstructionConfirm.onReadyToSetupPhase,
            //     searchTypes: [DeviceType.tuyaRemote]
        }, {
            name: ad.types.plug,
            categories: [AddDeviceCategory.ElectricityControlDevices],
            isAvailable: true,
            wifi: true,
            pluggable: true,
            icon: IconPlug, iconWhite: IconPlugWhite,
            resetInstruction: ad.resetInstruction.plug, resetImage: ImageResetPlug,
            resetInstructionConfirm: ad.resetInstructionConfirm.blinkedLikeFigure,
            searchTypes: [DeviceType.plug]
        }
    ], [ad, negotiatedProtocol]);

    const configByCaterory = useMemo(() => {
        const resultObject = {};
        for (let i = 0; i < deviceConfig.length; i++) {
            const conf = deviceConfig[i];
            for (let j = 0; j < conf.categories.length; j++) {
                const cat = conf.categories[j];
                if (resultObject[cat] === undefined) {
                    resultObject[cat] = {
                        name: ad.categories[cat],
                        types: []
                    };
                }
                resultObject[cat].types.push(conf);
            }
        }
        return resultObject;
    }, [ad, deviceConfig]);

    return { deviceConfig, configByCaterory };
}


// const categories = [
//     AddDeviceCategory.TouchSwitches,
//     AddDeviceCategory.SecurityDevices,
//     AddDeviceCategory.TemperatureControlDevices,
//     AddDeviceCategory.ElectricityControlDevices,
//     AddDeviceCategory.ConvenienceDevices,
// ];

// return categories.map(c => resultObject[c]);

export function getDeviceTypeIcon(deviceType) {
    switch (deviceType) {
        case DeviceType.lamp:
        case DeviceType.lampX1TwoWay:
        case DeviceType.dpst:
        case DeviceType.lampX3:
        case DeviceType.lampX4:
            return { icon: IconLamp, iconWhite: IconLampWhite };
        case DeviceType.scenarioSwitch:
            return { icon: IconScenarioSwitch, iconWhite: IconScenarioSwitchWhite };
        case DeviceType.curtain1Lamp2:
        case DeviceType.curtain:
            return { icon: IconCurtain, iconWhite: IconCurtainWhite };
        case DeviceType.evaporativeCooler:
            return { icon: IconCooler, iconWhite: IconCoolerWhite };
        case DeviceType.hinavaEcoThermostat:
        case DeviceType.sedna:
        case DeviceType.marsus:
            return { icon: IconTermostat, iconWhite: IconTermostatWhite };
        case DeviceType.multipurposeSensor:
            return { icon: IconMultipurposeSensor, iconWhite: IconMultipurposeSensorWhite };
        case DeviceType.wc:
            return { icon: IconWC, iconWhite: IconWCWhite };
        case DeviceType.doorbell:
            return { icon: IconDoorbell, iconWhite: IconDoorbellWhite };
        case DeviceType.elevator1:
            return { icon: IconElevator, iconWhite: IconElevatorWhite };
        case DeviceType.wifiDoorSensor:
        case DeviceType.doorSensor:
            return { icon: IconDoor, iconWhite: IconDoorWhite };
        case DeviceType.wifiMotionSensor:
        case DeviceType.motion:
            return { icon: IconMotion, iconWhite: IconMotionWhite };
        case DeviceType.relay4:
            return { icon: IconRelay, iconWhite: IconRelayWhite };
        case DeviceType.siren:
            return { icon: IconSiren, iconWhite: IconSirenWhite };
        case DeviceType.amplifier:
            return { icon: IconAudioController, iconWhite: IconAudioControllerWhite };
        case DeviceType.remote:
            return { icon: IconRemote, iconWhite: IconRemoteWhite };
        case DeviceType.plug:
            return { icon: IconPlug, iconWhite: IconPlugWhite };
        default:
            return undefined;
    }
}